import { createAmericanAirlinesLoginUrl } from "Services/urls";
import { COMMON_DAYJS_FORMAT, FLIGHT_SELECT_URL, ROLE_CODES, USA_DOLLAR_CODE } from "Services/constants";
import { logOutInline } from "Services/common";
import { usePreviousSearch } from "Components/searchbox/searchTab/flights/usePreviousSearch";

type PreviousSearch = ReturnType<typeof usePreviousSearch>;
import { CurrentSearch } from "Shared/managers/current-search-manager/CurrentSearch";
import { DynamicSettings } from "Components/admin/DynamicSettings";
import SearchRequest from "Shared/models/SearchRequest";
import url from "url";
import dayjs from "dayjs";

export interface FlightSearchRequestDto {
	adults: number;
	children: number;
	culture: string;
	currency: string;
	departureDate: dayjs.Dayjs;
	destinationCode: string;
	infants: number;
	isOneWay: boolean;
	isRedemptionSearch: boolean;
	originCode: string;
	promoCodes: string[];
	returnDate: dayjs.Dayjs | undefined;
	selectedPriceInbound: string;
	selectedPriceOutbound: string;
	teens: number;
}

export const buildFlightSearchRequest = (dto: FlightSearchRequestDto): SearchRequest => {
	const cultureElements = dto.culture.split("-", 2);
	const rc = cultureElements.length === 2 ? cultureElements[1] : "";

	let req: SearchRequest = {
		o1: dto.originCode,
		d1: dto.destinationCode,
		dd1: dto.departureDate.format(COMMON_DAYJS_FORMAT),
		ADT: dto.adults + dto.teens,
		CHD: dto.children,
		inl: dto.infants,
		r: false,
		s: true,
		mon: true,
		cur: !dto.isRedemptionSearch ? dto.currency : USA_DOLLAR_CODE,
		culture: dto.culture,
		pc: !dto.isRedemptionSearch ? dto.promoCodes[0] || "" : "",
		rc,
		selectedPriceOutbound: dto.selectedPriceOutbound,
		selectedPriceInbound: "",
		isRedemptionFlow: dto.isRedemptionSearch || false,
	};

	if (!dto.isOneWay) {
		req = {
			...req,
			dd2: dto.returnDate.format(COMMON_DAYJS_FORMAT),
			selectedPriceInbound: dto.selectedPriceInbound,
			r: true,
		};
	}

	if (dto.promoCodes[1]) {
		req = { ...req, pc2: !dto.isRedemptionSearch ? dto.promoCodes[1] : "" };
	}

	return req;
};

export const navigateToFlightSearch = (requestUrl: string, dynamicSettings: DynamicSettings) => {
	if (dynamicSettings.IsQueueItOn) {
		const newLocationUrl = encodeURIComponent(
			url.resolve(window.JetSmart.Settings.BookingUrl, `${FLIGHT_SELECT_URL}${requestUrl}`)
		);
		window.location.href = dynamicSettings.QueueItPrefix + newLocationUrl;
	} else {
		location.href = url.resolve(window.JetSmart.Settings.BookingUrl, `${FLIGHT_SELECT_URL}${requestUrl}`);
	}
};

export const redirectToAmericanAirlinesLogin = async (culture: string, roleCode: string | undefined) => {
	if (roleCode !== ROLE_CODES.WEB_ANONYMOUS) {
		await logOutInline();
	}
	window.location.href = createAmericanAirlinesLoginUrl(culture);
};

export interface SavePreviousSearchDto {
	currentSearch: CurrentSearch;
	selectedPriceOutbound: string;
	selectedPriceInbound: string;
}

export const savePreviousSearch = (previousSearch: PreviousSearch, dto: SavePreviousSearchDto) => {
	previousSearch.save({
		departureDate: dto.currentSearch.departureDate.format(COMMON_DAYJS_FORMAT),
		destination: dto.currentSearch.destinationCity.code,
		isOneWay: dto.currentSearch.isOneWay,
		origin: dto.currentSearch.originCity.code,
		passengers: {
			adults: dto.currentSearch.passengers.adults,
			children: dto.currentSearch.passengers.children,
			infants: dto.currentSearch.passengers.infants,
			teens: dto.currentSearch.passengers.teens,
		},
		returnDate: dto.currentSearch.returnDate?.format(COMMON_DAYJS_FORMAT) || "",
	});
};

export const handleReloadSearch = async (
	e: MouseEvent,
	previousSearch: PreviousSearch,
	reloadSearch: (saved: ReturnType<PreviousSearch["load"]>, promoCodes: string[]) => void,
	promoCodes: string[]
) => {
	e.preventDefault();

	const value = previousSearch.load();
	if (!value) return;

	try {
		reloadSearch(value, promoCodes);
	} catch {
		// Do nothing
	}
};
